import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import Ansi from "@cocalc/ansi-to-react";
import { Activity } from "@packages/client";
import { WingIcon } from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import stripAnsi from "strip-ansi";

import { loadLogFromActivity, Log } from "Reducers/log";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./styles";

interface Props {
  activity: Activity;
  openModal: () => void;
}

const logs2Text = (logs: Log[]) => {
  return logs.reduce((logText, jsLog) => {
    if (!jsLog?.data) {
      return logText;
    }
    return `${logText} ${stripAnsi(jsLog.data.message)}`;
  }, "");
};

const PopupLog: React.FC<Props> = ({ activity, openModal }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [loadedLog, setLoadedLog] = useState(false);
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const [linesSelected, setLinesSelected] = useState<{
    start: null | number;
    end: null | number;
    scrollTo: boolean;
  }>({
    start: null,
    end: null,
    scrollTo: false
  });
  const logWrapper = useRef<HTMLPreElement>(null);
  const listLog = useRef<VirtuosoHandle>(null);
  const loading = useAppSelector(({ log }) =>
    activity ? (log[activity.id]?.loading ?? true) : true
  );
  const jsLog = useAppSelector(({ log }) =>
    activity ? (log[activity.id]?.log ?? []) : []
  );
  const logs = useMemo(() => {
    if (!jsLog.length) return "";
    return logs2Text(jsLog);
  }, [jsLog]);
  const offsetHeight = logWrapper.current?.offsetHeight ?? 0;
  const height = offsetHeight > 0 ? offsetHeight - 10 : 50;

  const isSelected = (index: number) => {
    const { start, end } = linesSelected;
    if (!end) {
      return start === index;
    }
    return start! <= index && index <= end;
  };

  const showTimeStamp = (index: number) => {
    const { start, end } = linesSelected;
    return index === start || index === end;
  };

  const selectLine = (e: React.MouseEvent, line: number) => {
    e.preventDefault();
    e.stopPropagation();
    const { start, end } = linesSelected;

    if (start === line) {
      setLinesSelected({ start: end, end: null, scrollTo: false });
      return;
    }

    if (e.shiftKey) {
      if (!start && !end) {
        setLinesSelected({ start: line, end: null, scrollTo: false });
      } else if (line < start!) {
        setLinesSelected({ start: line, end: start, scrollTo: false });
      } else {
        setLinesSelected({ ...linesSelected, end: line, scrollTo: false });
      }
    } else {
      setLinesSelected({ start: line, end: null, scrollTo: false });
    }
  };

  const scrollTo = useCallback(
    (index: number | "LAST") => {
      listLog.current?.scrollToIndex({
        index,
        align: "center"
      });
      setLinesSelected({ ...linesSelected, scrollTo: false });
    },
    [linesSelected]
  );

  const rowRenderer = (index: number, log: Log | undefined) => {
    const isError = log?.data.message.trim().startsWith("E: ");

    return (
      <>
        {log ? (
          <S.Line
            selected={isSelected(index)}
            showTimeStamp={showTimeStamp(index)}
            $isError={isError}
          >
            <S.LineNumber
              onClick={e => {
                selectLine(e, index);
              }}
            >
              {index + 1}
            </S.LineNumber>
            <Ansi>{log.data.message}</Ansi>
            <S.TimeStamp>
              {index !== linesSelected.end ? (
                <>{moment(log.data.timestamp).format("LTS")}</>
              ) : (
                <>
                  {moment
                    .duration(
                      moment(log.data.timestamp).diff(
                        moment(jsLog[linesSelected.start!]?.data?.timestamp)
                      )
                    )
                    .asSeconds()}{" "}
                  sec
                </>
              )}
            </S.TimeStamp>
          </S.Line>
        ) : (
          <S.Spacer />
        )}
      </>
    );
  };

  useEffect(() => {
    if (!hasScrolledDown && !loading) {
      scrollTo("LAST");
      setHasScrolledDown(true);
    }
  }, [hasScrolledDown, linesSelected, loading, scrollTo]);

  useEffect(() => {
    if (!loadedLog && activity?.id) {
      dispatch(loadLogFromActivity({ activity }));
      setLoadedLog(true);
    }
  }, [activity, dispatch, loadedLog]);

  return (
    <S.LogContainer>
      <S.LogPre ref={logWrapper} showLinesNumber={!loading && logs?.length > 0}>
        <S.Container>
          {loading ? (
            <S.DotDotDot>{intl.formatMessage({ id: "loading" })}</S.DotDotDot>
          ) : logs?.length > 0 ? (
            <Virtuoso
              ref={listLog}
              style={{ height: height }}
              data={jsLog}
              itemContent={rowRenderer}
              overscan={200}
            />
          ) : (
            <S.Empty>{intl.formatMessage({ id: "log_empty" })}</S.Empty>
          )}
        </S.Container>
      </S.LogPre>
      {!loading && (
        <S.IconContainer onClick={openModal}>
          <WingIcon />
        </S.IconContainer>
      )}
    </S.LogContainer>
  );
};
export default PopupLog;
